import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Badge, Button, Form, Row, Col, Spinner, Modal } from 'react-bootstrap';
import { FiPlus, FiCheck, FiCalendar, FiEdit2, FiTrash2, FiVideo, FiMapPin, FiX, FiCheckSquare } from 'react-icons/fi';
import { toast } from 'react-toastify';
import hearingsService, { Hearing } from '../../services/hearings.service';

const HearingsList: React.FC = () => {
  const [hearings, setHearings] = useState<Hearing[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: ''
  });

  // Modal states
  const [showOutcomeModal, setShowOutcomeModal] = useState(false);
  const [showPostponeModal, setShowPostponeModal] = useState(false);
  const [selectedHearing, setSelectedHearing] = useState<Hearing | null>(null);
  const [outcomeData, setOutcomeData] = useState({ outcome: '', notes: '' });
  const [postponeData, setPostponeData] = useState({ new_date: '', new_time: '', reason: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadHearings();
  }, [filters]);

  const loadHearings = async () => {
    try {
      setLoading(true);
      const result = await hearingsService.list({
        status: filters.status || undefined
      });
      setHearings(result.data);
    } catch (error) {
      toast.error('Error al cargar las audiencias');
    } finally {
      setLoading(false);
    }
  };

  const handleOutcome = async () => {
    if (!selectedHearing) return;
    try {
      await hearingsService.setOutcome(selectedHearing.id, outcomeData.outcome, outcomeData.notes);
      toast.success('Resultado registrado');
      setShowOutcomeModal(false);
      setOutcomeData({ outcome: '', notes: '' });
      loadHearings();
    } catch (error) {
      toast.error('Error al registrar resultado');
    }
  };

  const handlePostpone = async () => {
    if (!selectedHearing) return;
    try {
      await hearingsService.postpone(selectedHearing.id, postponeData.new_date, postponeData.new_time, postponeData.reason);
      toast.success('Audiencia reprogramada');
      setShowPostponeModal(false);
      setPostponeData({ new_date: '', new_time: '', reason: '' });
      loadHearings();
    } catch (error) {
      toast.error('Error al reprogramar');
    }
  };

  const handleCancel = async (id: number) => {
    const reason = window.prompt('Razón de cancelación:');
    if (reason !== null) {
      try {
        await hearingsService.cancel(id, reason);
        toast.success('Audiencia cancelada');
        loadHearings();
      } catch (error) {
        toast.error('Error al cancelar');
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Eliminar esta audiencia?')) {
      try {
        await hearingsService.delete(id);
        toast.success('Audiencia eliminada');
        loadHearings();
      } catch (error) {
        toast.error('Error al eliminar');
      }
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      scheduled: 'primary',
      completed: 'success',
      rescheduled: 'warning',
      postponed: 'warning',
      cancelled: 'secondary'
    };
    const labels: Record<string, string> = {
      scheduled: 'Programada',
      completed: 'Realizada',
      rescheduled: 'Reprogramada',
      postponed: 'Aplazada',
      cancelled: 'Cancelada'
    };
    return <Badge bg={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  const getTypeBadge = (type: string) => {
    const labels: Record<string, string> = {
      initial: 'Inicial',
      evidence: 'Pruebas',
      judgment: 'Juzgamiento',
      conciliation: 'Conciliación',
      other: 'Otra'
    };
    return <Badge bg="outline-info" className="text-dark border">{labels[type] || type}</Badge>;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-CO', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeStr?: string) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  const isToday = (dateStr: string) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr.split('T')[0] === today;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h3 mb-0">Audiencias</h1>
        <Link to="/hearings/new" className="btn btn-primary">
          <FiPlus className="me-2" /> Nueva Audiencia
        </Link>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Label><FiCalendar className="me-1" /> Estado</Form.Label>
              <Form.Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Todos</option>
                <option value="scheduled">Programada</option>
                <option value="completed">Realizada</option>
                <option value="rescheduled">Reprogramada</option>
                <option value="cancelled">Cancelada</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <Button variant="outline-secondary" onClick={() => setFilters({ status: '' })}>
                Limpiar
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : hearings.length === 0 ? (
            <div className="text-center py-5 text-muted">
              No hay audiencias registradas
            </div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Hora</th>
                  <th>Audiencia</th>
                  <th>Caso</th>
                  <th>Tipo</th>
                  <th>Lugar</th>
                  <th>Estado</th>
                  <th style={{ width: '150px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {hearings.map((hearing) => (
                  <tr key={hearing.id} className={isToday(hearing.scheduled_date) ? 'table-warning' : ''}>
                    <td>
                      <strong>{formatDate(hearing.scheduled_date)}</strong>
                      {isToday(hearing.scheduled_date) && <Badge bg="warning" className="ms-2">HOY</Badge>}
                    </td>
                    <td>{formatTime(hearing.scheduled_time)}</td>
                    <td>
                      <strong>{hearing.title}</strong>
                    </td>
                    <td>
                      {hearing.case_number ? (
                        <Link to={`/cases/${hearing.case_id}`}>
                          {hearing.case_number}
                          <div className="text-muted small">{hearing.client_name}</div>
                        </Link>
                      ) : '-'}
                    </td>
                    <td>{getTypeBadge(hearing.hearing_type)}</td>
                    <td>
                      {hearing.virtual_link ? (
                        <a href={hearing.virtual_link} target="_blank" rel="noopener noreferrer" className="text-info">
                          <FiVideo className="me-1" /> Virtual
                        </a>
                      ) : hearing.location ? (
                        <span><FiMapPin className="me-1" /> {hearing.location}</span>
                      ) : '-'}
                    </td>
                    <td>{getStatusBadge(hearing.status)}</td>
                    <td>
                      {hearing.status === 'scheduled' ? (
                        <>
                          <Link 
                            to={`/hearings/${hearing.id}/checklist`} 
                            className="btn btn-outline-info btn-sm me-1" 
                            title="Checklist"
                          >
                            <FiCheckSquare />
                          </Link>
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="me-1"
                            title="Registrar Resultado"
                            onClick={() => {
                              setSelectedHearing(hearing);
                              setShowOutcomeModal(true);
                            }}
                          >
                            <FiCheck />
                          </Button>
                          <Button
                            variant="outline-warning"
                            size="sm"
                            className="me-1"
                            title="Reprogramar"
                            onClick={() => {
                              setSelectedHearing(hearing);
                              setShowPostponeModal(true);
                            }}
                          >
                            <FiCalendar />
                          </Button>
                          <Link to={`/hearings/${hearing.id}/edit`} className="btn btn-outline-primary btn-sm me-1" title="Editar">
                            <FiEdit2 />
                          </Link>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            title="Cancelar"
                            onClick={() => handleCancel(hearing.id)}
                          >
                            <FiX />
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          title="Eliminar"
                          onClick={() => handleDelete(hearing.id)}
                        >
                          <FiTrash2 />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Outcome Modal */}
      <Modal show={showOutcomeModal} onHide={() => setShowOutcomeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Registrar Resultado</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Resultado</Form.Label>
            <Form.Select
              value={outcomeData.outcome}
              onChange={(e) => setOutcomeData({ ...outcomeData, outcome: e.target.value })}
            >
              <option value="">Seleccione...</option>
              <option value="favorable">Favorable</option>
              <option value="unfavorable">Desfavorable</option>
              <option value="partial">Parcial</option>
              <option value="postponed">Aplazada en audiencia</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Notas</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={outcomeData.notes}
              onChange={(e) => setOutcomeData({ ...outcomeData, notes: e.target.value })}
              placeholder="Observaciones de la audiencia..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowOutcomeModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleOutcome} disabled={!outcomeData.outcome}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Postpone Modal */}
      <Modal show={showPostponeModal} onHide={() => setShowPostponeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reprogramar Audiencia</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nueva Fecha *</Form.Label>
            <Form.Control
              type="date"
              value={postponeData.new_date}
              onChange={(e) => setPostponeData({ ...postponeData, new_date: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Nueva Hora</Form.Label>
            <Form.Control
              type="time"
              value={postponeData.new_time}
              onChange={(e) => setPostponeData({ ...postponeData, new_time: e.target.value })}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Razón</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={postponeData.reason}
              onChange={(e) => setPostponeData({ ...postponeData, reason: e.target.value })}
              placeholder="Motivo del aplazamiento..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPostponeModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handlePostpone} disabled={!postponeData.new_date}>
            Reprogramar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default HearingsList;
